/**
 * UranIA PA — Cutover gate dashboard loader (US-023).
 *
 * Espone i tipi + il loader per la pagina `/admin/cutover-gates`. La
 * dashboard è una **living document** mantenuta manualmente dal PM
 * cutover + Sponsor sul file JSON sorgente:
 *   `apps/spa/src/lib/admin/cutover-gates-fase-5.json`
 *
 * Il file è validato a build-time da `tests/unit/test_cutover_gates_schema.py`
 * contro `docs/security/schemas/cutover-gates.schema.json` (gate CI Fase 5
 * US-023). Quando si aggiorna lo stato di un gate o si raccoglie una firma:
 *
 *   1. PM modifica il JSON (incrementa `compiled_at`, sostituisce
 *      `compiled_by`, cambia gate `status` / `evidence_url`, popola
 *      `signatures.<ruolo>` con `signed_by` + `signed_at` +
 *      `signature_evidence_url`).
 *   2. CI rivalida lo schema.
 *   3. PR review 2-eyes (DPO + Tech Lead consigliato).
 *   4. Merge → la dashboard /admin/cutover-gates riflette i nuovi stati.
 *
 * Riferimenti:
 *   - analisi `analisi_per_nuova_versione.md` §25.5 — 8 gate di no-go.
 *   - PRD `tasks/prd-fase-5-hardening-psn.md` US-F5-09.
 *   - ADR-021 §Sunset condition (condizioni #1, #3, #6, #9 mappate sui
 *     gate 1, 3, 8, 2 rispettivamente).
 *
 * Boundary architetturale:
 *   - La dashboard è **read-only** per la SPA (admin role guard come
 *     `tenants` / `skills`). Nessuna mutazione lato client: lo stato
 *     dei gate è single source of truth nel JSON committato.
 *   - L'autorizzazione reale resta server-side via API Gateway (qui il
 *     guard SPA è UX, non sicurezza — come per US-028 / US-029).
 *   - Il file JSON è importato via `resolveJsonModule: true` (cfr.
 *     `tsconfig.app.json`) → tree-shake friendly + tipo inferito.
 */

import gatesData from './cutover-gates-fase-5.json' with { type: 'json' };

export type CutoverGateStatus = 'ok' | 'warning' | 'blocked' | 'pending';

export type CutoverSignatureRole =
  | 'DPO'
  | 'Compliance Officer'
  | 'Tech Lead'
  | 'Sponsor';

export type CutoverSignatureStatus = 'signed' | 'pending';

export interface CutoverGateDto {
  readonly index: number;
  readonly id: string;
  readonly title_it: string;
  readonly status: CutoverGateStatus;
  readonly evidence_url: string;
  readonly evidence_label?: string;
  readonly owner: string;
  readonly target_iso_date: string;
  readonly notes?: string;
}

export interface CutoverSignatureDto {
  readonly role: CutoverSignatureRole;
  readonly status: CutoverSignatureStatus;
  readonly signed_by?: string;
  readonly signed_at?: string;
  readonly signature_evidence_url?: string;
}

export interface CutoverSignaturesDto {
  readonly dpo: CutoverSignatureDto;
  readonly compliance_officer: CutoverSignatureDto;
  readonly tech_lead: CutoverSignatureDto;
  readonly sponsor: CutoverSignatureDto;
}

export interface CutoverGatesDto {
  readonly schema_version: string;
  readonly phase: string;
  readonly branch: string;
  readonly compiled_at: string;
  readonly compiled_by: string;
  readonly gates: readonly CutoverGateDto[];
  readonly signatures: CutoverSignaturesDto;
}

/**
 * Strip `$schema` (presente solo per IDE/json-schema validator) e
 * restituisce un payload puro `CutoverGatesDto`.
 *
 * Cast finale `as CutoverGatesDto` accettabile in quanto la validazione
 * reale del payload contro lo schema avviene a build-time
 * (`tests/unit/test_cutover_gates_schema.py`). Nessuna validazione
 * runtime (no zod / valibot): il file è committato e governance-controlled.
 */
function loadCutoverGates(): CutoverGatesDto {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { $schema, ...rest } = gatesData as { $schema?: string } & Record<string, unknown>;
  return rest as unknown as CutoverGatesDto;
}

export const CUTOVER_GATES_FASE_5: CutoverGatesDto = loadCutoverGates();

/**
 * True solo se TUTTI gli 8 gate sono `ok` AND tutte le 4 firme sono
 * `signed`. Il banner "go-live abilitato" si attiva esclusivamente in
 * questa condizione (US-023 AC: "Green banner appears only when all
 * 8 ✅ AND all 4 signed").
 */
export function isGoLiveAuthorized(data: CutoverGatesDto): boolean {
  const allGatesOk = data.gates.every((gate) => gate.status === 'ok');
  if (!allGatesOk) {
    return false;
  }
  const sigs = data.signatures;
  return (
    sigs.dpo.status === 'signed' &&
    sigs.compliance_officer.status === 'signed' &&
    sigs.tech_lead.status === 'signed' &&
    sigs.sponsor.status === 'signed'
  );
}

/**
 * Conta i gate per stato — utile per il riepilogo header dashboard.
 */
export function countGatesByStatus(
  data: CutoverGatesDto,
): Readonly<Record<CutoverGateStatus, number>> {
  const counters: Record<CutoverGateStatus, number> = {
    ok: 0,
    warning: 0,
    blocked: 0,
    pending: 0,
  };
  for (const gate of data.gates) {
    counters[gate.status] += 1;
  }
  return counters;
}

/**
 * Conta le firme `signed`. Da combinare con `countGatesByStatus` per
 * il riepilogo "X/8 gate verdi · Y/4 firme apposte".
 */
export function countSignedSignatures(data: CutoverGatesDto): number {
  const sigs = data.signatures;
  return (
    (sigs.dpo.status === 'signed' ? 1 : 0) +
    (sigs.compliance_officer.status === 'signed' ? 1 : 0) +
    (sigs.tech_lead.status === 'signed' ? 1 : 0) +
    (sigs.sponsor.status === 'signed' ? 1 : 0)
  );
}
